// The reveal grid (lane B7 S14; CR-024 the sealed reveal, CR-071 the flags, CR-007 the summary's scorecards). Until the
// reveal every card is sealed ("No other scorecards visible before submission."; plan D13 "scores stay sealed until the
// reveal"), and after it a card that was never submitted stays sealed with its status word, Waived included (DC-183).
// A revealed, submitted card draws its scores, its recommendation and its flags (vocab FLAG_KINDS: at Sr Director and
// below a No Hire or two ratings of 2 or below is a *Concern flag*; at VP+ the VP+ rule). The Coordinator's projection
// is sealed throughout: no score and no flag ever reaches it.
// B14 (R5, DC-249): a revealed card's recommendation word and tone come from the design system's mapping (C5), never a
// guess over the stored value; its average core score and band dot are the summary row's (C3), never computed here. A
// card recorded on the earlier scale keeps its word with the earlier-scale marker and its reservation; Consider Other
// Role (and a No Hire's follow-up) shows the suggested role. A snapshot frozen before B14 draws "—" for the average.

import type { ReactNode } from "react";
import {
  Card, LEVEL_RULE_LABELS, Pill, PLAYBOOK_LABELS, PLAYBOOK_TEMPLATES, RECOMMENDATION_LABELS, SCORECARD_STATUS, ScoreRow, SealedCard, VisuallyHidden,
  type ScoreItem, type ScoreValue,
} from "@/mc";
import { hisRoundLabel } from "../score/round";
import { SUMMARY_LABELS, SUMMARY_RULES } from "../summary/labels";
import type { ReadinessOut } from "./Readiness";
import { averageOf, bandOf, recoWord, type RubricBand } from "./reco";
import type { Attendee } from "./types";

export const SCORECARD_DISPLAY = ["not_started", "in_progress", "submitted", "past_due", "waived"] as const;
export type ScorecardDisplay = (typeof SCORECARD_DISPLAY)[number];
export const FLAG_KINDS = ["no_hire_recommendation", "two_low_ratings", "vp_no_hire", "vp_low_rating", "legacy_two_2s"] as const;
export type FlagKind = (typeof FLAG_KINDS)[number];

export interface GridCard {
  key: string;
  author: string;
  authorId?: string | null | undefined;
  round?: string | null | undefined;
  scorecardId?: string | null | undefined;
  assigned: readonly string[];
  status: ScorecardDisplay;
  scores: readonly ScoreItem[];
  /** The stored recommendation (any value the mapping knows; C5). */
  recommendation?: string | null | undefined;
  flags: readonly FlagKind[];
  /** The card's average core score ("4.50", C3); null when the row carries none (a frozen snapshot draws "—"). */
  coreAverage?: string | null | undefined;
  /** The band that average falls in (C9), highlighted only when the server says so. */
  rubricBand?: RubricBand | null | undefined;
  /** An earlier-scale card's reservation text. */
  reservation?: string | null | undefined;
  /** Consider Other Role, or a No Hire's "consider for another position" answered yes: the role suggested. */
  suggestedRole?: string | null | undefined;
}

export const DISPLAY_WORD: Readonly<Record<ScorecardDisplay, string>> = {
  not_started: SCORECARD_STATUS.notStarted,
  in_progress: SCORECARD_STATUS.inProgress,
  submitted: SCORECARD_STATUS.submitted,
  past_due: SCORECARD_STATUS.pastDue,
  waived: SCORECARD_STATUS.waived,
};

type Row = Record<string, unknown>;
const isDisplay = (v: unknown): v is ScorecardDisplay => (SCORECARD_DISPLAY as readonly unknown[]).includes(v);
const isFlag = (v: unknown): v is FlagKind => (FLAG_KINDS as readonly unknown[]).includes(v);

/** S03's display status when sent; otherwise the V1 row status in the scorecard chain. */
export function displayOf(row: Row): ScorecardDisplay {
  for (const key of ["display_status", "scorecard_status"]) if (isDisplay(row[key])) return row[key] as ScorecardDisplay;
  switch (row.status) {
    case "submitted":
    case "revised":
    case "recorded from paper — unconfirmed":
      return "submitted";
    case "waived":
      return "waived";
    case "past_due":
      return "past_due";
    case "in_progress":
      return "in_progress";
    default:
      return "not_started";
  }
}

/** S04's per-row flag codes when sent; the V1 two-2s flag otherwise. */
export function flagsOf(row: Row): FlagKind[] {
  if (Array.isArray(row.flags)) return (row.flags as unknown[]).filter(isFlag);
  return row.two_2s === true ? ["legacy_two_2s"] : [];
}

/**
 * CR-071: the candidate's concern flags as `debrief.open` projects them (codes only, per card; the server sends a reader
 * only the flags it may see), added to each card's own. A flag names its card by scorecard id.
 */
export function withConcernFlags(
  cards: readonly GridCard[],
  flags: readonly { kind: string; scorecard_id: string }[] | null | undefined,
): GridCard[] {
  if (!flags?.length) return [...cards];
  return cards.map((card) => {
    const extra = flags.filter((f) => card.scorecardId && f.scorecard_id === card.scorecardId).map((f) => f.kind).filter(isFlag);
    return extra.length ? { ...card, flags: [...new Set([...card.flags, ...extra])] } : card;
  });
}

/** The stored value when the mapping knows it; an unknown value is no recommendation, never a guess. */
function recommendationOf(value: unknown): string | null {
  return recoWord(value)?.value ?? null;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value : null);

/** The row's consider-other-role block (C3: filled for No Hire and Consider Other Role): the role when answered yes. */
function suggestedRoleOf(row: Row): string | null {
  const block = row.consider_other_role as { answer?: unknown; suggested_role?: unknown } | null | undefined;
  return block && block.answer === "yes" ? text(block.suggested_role) : null;
}

function scoresOf(row: Row, labels: Readonly<Record<string, string>>): ScoreItem[] {
  const ratings = (row.ratings ?? {}) as Record<string, { state?: string; score?: unknown }>;
  return Object.entries(ratings).map(([dim, rating]) => {
    const n = typeof rating?.score === "number" ? rating.score : null;
    const score: ScoreValue | "na" = n !== null && n >= 1 && n <= 5 ? (Math.round(n) as ScoreValue) : "na";
    return { label: labels[dim] ?? dim, score };
  });
}

/** The values and skills a person was assigned, read back from the snapshot's coverage (value → round, author). */
function assignedOf(snapshot: Row, authorId: string | null, author: string): string[] {
  const coverage = (snapshot.coverage ?? {}) as Record<string, { author?: string; author_id?: string }[]>;
  const labels = (snapshot.dimension_labels ?? {}) as Record<string, string>;
  return Object.entries(coverage)
    .filter(([, by]) => (by ?? []).some((c) => (authorId && c.author_id === authorId) || c.author === author))
    .map(([dim]) => labels[dim] ?? dim);
}

/** The grid from the summary snapshot (the compiler's live view, or the frozen sheet after the reveal). */
export function cardsFromSnapshot(snapshot: Row | null | undefined): GridCard[] {
  if (!snapshot) return [];
  const labels = (snapshot.dimension_labels ?? {}) as Record<string, string>;
  return ((snapshot.rows ?? []) as Row[])
    .filter((row) => row.is_screen !== true)
    .map((row, index) => {
      const author = String(row.author ?? "");
      const authorId = typeof row.author_id === "string" ? row.author_id : null;
      return {
        key: String(row.scorecard_id ?? `${authorId ?? author}-${index}`),
        author,
        authorId,
        round: typeof row.round === "string" ? hisRoundLabel(row.round) : null,
        scorecardId: typeof row.scorecard_id === "string" ? row.scorecard_id : null,
        assigned: assignedOf(snapshot, authorId, author),
        status: displayOf(row),
        scores: scoresOf(row, labels),
        recommendation: recommendationOf(row.recommendation),
        flags: flagsOf(row),
        coreAverage: averageOf(row.core_average),
        rubricBand: bandOf(row.rubric_band),
        reservation: text(row.reservation),
        suggestedRole: suggestedRoleOf(row),
      };
    });
}

/**
 * Before the reveal, for a reader with no snapshot: one sealed card per required interviewer, Submitted unless the
 * readiness read still names their scorecard as missing (the contracts carry no per-card status before the reveal).
 */
export function cardsFromRoster(attendees: readonly Attendee[], readiness?: ReadinessOut | null): GridCard[] {
  const missing = new Set((readiness?.controls ?? []).filter((c) => c.code === "scorecards_submitted").map((c) => c.author_name ?? ""));
  return attendees.filter((a) => a.role === "interviewer").map((a) => ({
    key: a.person_id, author: a.name, authorId: a.person_id, assigned: [], scores: [], flags: [],
    status: missing.has(a.name) ? "not_started" : "submitted",
  }));
}

function FlagPills({ flags }: { flags: readonly FlagKind[] }) {
  const concern = flags.some((f) => f === "no_hire_recommendation" || f === "two_low_ratings" || f === "legacy_two_2s");
  const vp = flags.some((f) => f === "vp_no_hire" || f === "vp_low_rating");
  return (
    <>
      {concern ? <Pill tone="flag">{LEVEL_RULE_LABELS.concernFlag}</Pill> : null}
      {vp ? <Pill tone="red">{LEVEL_RULE_LABELS.vpPlus}</Pill> : null}
    </>
  );
}

/** The recommendation word in its tone (C5); an earlier-scale card carries the earlier-scale marker beside it. */
export function RecoPills({ card }: { card: Pick<GridCard, "recommendation"> }) {
  const word = recoWord(card.recommendation);
  if (!word) return null;
  return (
    <>
      <Pill tone={word.tone}>{word.label}</Pill>
      {word.earlierScale ? <Pill tone="neutral">{RECOMMENDATION_LABELS.earlierScale}</Pill> : null}
    </>
  );
}

/** The card's average core score "x.xx / 5" with its band dot (the pill's dot, in the band's tone only when the server
 * highlights it); "—" when the row carries no average (a snapshot frozen before B14). Never computed here. */
export function CoreAverage({ card }: { card: Pick<GridCard, "coreAverage" | "rubricBand"> }) {
  const band = card.rubricBand ?? null;
  const tone = band?.highlight ? recoWord(band.key)?.tone ?? "neutral" : "neutral";
  return (
    <Pill tone={tone}>
      <VisuallyHidden>{PLAYBOOK_LABELS.averageCoreScore}{" "}</VisuallyHidden>
      {card.coreAverage ? PLAYBOOK_TEMPLATES.outOfFive({ average: card.coreAverage }) : "—"}
      {band ? <VisuallyHidden>{" · "}{band.text}</VisuallyHidden> : null}
    </Pill>
  );
}

/** Under an open card: the suggested role, and an earlier-scale card's reservation. */
function CardNotes({ card }: { card: GridCard }) {
  if (!card.suggestedRole && !card.reservation) return null;
  return (
    <div className="mc-stack">
      {card.suggestedRole ? <p className="mc-hint">{PLAYBOOK_LABELS.suggestedRole}{": "}{card.suggestedRole}</p> : null}
      {card.reservation ? <p className="mc-hint">{card.reservation}</p> : null}
    </div>
  );
}

export interface RevealGridProps {
  cards: readonly GridCard[];
  /** Before the reveal: every card sealed. */
  sealed: boolean;
  /** "status": the Coordinator's projection, sealed whatever the room's state. */
  projection?: "full" | "status" | undefined;
  /** The reveal control (or nothing), in the card's header. */
  action?: ReactNode | undefined;
  /** Per card: the S15 dialogs' triggers (revise own score, waive, correct). */
  cardActions?: ((card: GridCard) => ReactNode) | undefined;
  loading?: boolean | undefined;
}

export function RevealGrid({ cards, sealed, projection = "full", action, cardActions, loading }: RevealGridProps) {
  const allSealed = sealed || projection === "status";
  return (
    <Card title={SUMMARY_LABELS.scorecards} count={cards.length} actions={action} loading={loading}>
      <div className="mc-stack">
        {cards.map((card) => {
          const person = { name: card.author, hueKey: card.authorId ?? card.author, sub: card.round ?? undefined };
          const open = !allSealed && card.status === "submitted";
          return (
            <div key={card.key} className="mc-stack">
              {open ? (
                <>
                  <ScoreRow
                    person={person}
                    scores={card.scores}
                    right={<><RecoPills card={card} /><CoreAverage card={card} /><FlagPills flags={card.flags} /></>}
                  />
                  <CardNotes card={card} />
                </>
              ) : (
                <SealedCard person={person} assigned={card.assigned} status={DISPLAY_WORD[card.status]} />
              )}
              {projection === "full" ? cardActions?.(card) : null}
            </div>
          );
        })}
        {allSealed ? <p className="mc-hint">{SUMMARY_RULES.sealedBeforeSubmission}</p> : null}
      </div>
    </Card>
  );
}
